import type { ConfirmDialogProps } from '@/components/ui/ConfirmDialog'
import type {
    CompanyAddFormValues,
    CompanyEditTabDefinition,
    CompanyEditTabId,
    PaymentAddrFieldKey,
    ActivityInitialPagination,
} from '../types'

export * from './tooltips'

/**
 * Company module constants — business values, labels, session keys, limits, and tab config.
 * Keep UI-only Tailwind helpers in component/style files; keep Zod schemas in validators.
 */

/** Company confirms use the same Save Changes shell + action buttons as application. */
export const companyEditConfirmPresentation = {
    presentation: 'saveChanges',
    primaryFirst: true,
    actionStyle: 'application',
} as const satisfies Partial<ConfirmDialogProps>

/** Policies match Angular `company-edit.component.html`. */
export const COMPANY_EDIT_TABS: CompanyEditTabDefinition[] = [
    { id: 'companyinfo', label: 'Company Info', policy: 'company_info_view' },
    { id: 'executivesummary', label: 'Executive Summary', policy: 'company_info_view' },
    { id: 'contacts', label: 'Contacts', policy: 'company_contact_view' },
    { id: 'preferences', label: 'Preferences', policy: 'company_preference_view' },
    { id: 'communications', label: 'Communications', policy: 'company_communication_view' },
    { id: 'payments', label: 'Payments', policy: 'company_payment_view' },
    { id: 'integrations', label: 'Integrations', policy: 'company_integration_view' },
    { id: 'activity', label: 'Activity', policy: 'company_acitvitylog_view' },
    { id: 'config', label: 'Config' },
]

/** Tabs that don't use the header Save → `form[data-tab-id]` flow (inline saves / view-only). */
export const COMPANY_EDIT_TABS_WITHOUT_GLOBAL_FORM: CompanyEditTabId[] = []

// ── List / session ───────────────────────────────────────────────────────────

export const COMPANY_LIST_SESSION_STORAGE_KEY = 'companylist_filter'

export const COMPANY_LIST_PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

export const COMPANY_LIST_SEARCH_DEBOUNCE_MS = 400

export const COMPANY_LIST_DEBOUNCED_FILTER_KEYS = [
    'units_number',
    'liberty_id',
] as const

export const COMPANY_LIST_DEBOUNCE_MS = 1000

/** Angular `routeParams()`: only these roles honor `?favorite_companies`. */
export const COMPANY_LIST_FAVORITE_DEEP_LINK_ROLES = ['ROLE_LIBERTY_SALES', 'ROLE_ADMIN'] as const

export const DEFAULT_PAYMENT_LOGS_SESSION_KEY = 'defaultpaymentlogs_filter'

// ── Roles & labels ───────────────────────────────────────────────────────────

export const COMPANY_ROLE_NAMES: Record<string, string> = {
    ROLE_COMPANY_MANAGER: 'Company Manager',
    ROLE_COMPANY_ADMIN: 'Company Admin',
    ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
}

export const PROPERTY_STAFF_ROLE_NAMES: Record<string, string> = {
    ROLE_PROPERTY_MANAGER: 'Property Manager',
    ROLE_PROPERTY_AGENT: 'Leasing Consultant',
    ROLE_PROPERTY_ADMIN: 'Property Assistant',
    ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
}

/** Angular `user-management-add` / Property Contacts: Finance Manager applies to these roles only. */
export const ADD_USER_FIN_MGR_ROLES = [
    'ROLE_COMPANY_ADMIN',
    'ROLE_COMPANY_MANAGER',
    'ROLE_PROPERTY_ADMIN',
    'ROLE_PROPERTY_MANAGER',
] as const

/** Angular gates executive-summary questions to ROLE_ADMIN and ROLE_LIBERTY_SALES only. */
export const EXEC_SUMMARY_EDITABLE_ROLES = ['ROLE_ADMIN', 'ROLE_LIBERTY_SALES'] as const

export const TRANSFER_LOG_DEFAULT_USER_FILTER_ROLES = [
    'ROLE_UNDERWRITER',
    'ROLE_CUSTOMER_SERVICE',
    'ROLE_CUSTOMER_SERVICE_MANAGER',
] as const

// ── Add user validation ──────────────────────────────────────────────────────

export const ADD_USER_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Same pattern as Angular `UserManagementAddComponent` first_name / last_name validators. */
export const ADD_USER_NAME_PATTERN =
    /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>/?`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>/?`~\\-]+){0,2}(?<! )$/

// ── Communication log labels (Angular CRM lists) ─────────────────────────────

export const COMMUNICATION_TYPES: Record<string, string> = {
    PHONE_CALL: 'Phone Call',
    IN_PERSON: 'In Person',
    EMAIL: 'Email',
    INSTANT_MESSAGE: 'Instant Message',
    WEBINAR: 'Webinar',
    TRADE_SHOW: 'Trade Show',
    OTHER: 'Other',
}

export const COMMUNICATION_CATEGORY: Record<string, string> = {
    INITIAL_CONTACT: 'Initial Contact',
    FOLLOW_UP: 'Follow-Up',
    TRAINING: 'Training',
    SYSTEM_HELP: 'System Help',
    PROSPECT: 'Prospect',
    PROPERTY_VISIT: 'Property Visit',
    ZERO_APPS: 'Zero Apps',
    MONTHLY_REPORTING: 'Monthly Reporting',
    OTHER: 'Other',
}

export const COMMUNICATION_DIRECTION: Record<string, string> = {
    IN: 'In',
    OUT: 'Out',
}

// ── Communications tab options ─────────────────────────────────────────────────

export const REPORT_FREQUENCIES = [
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Monthly', value: 'MONTHLY' },
    { label: 'None', value: 'NONE' },
] as const

export const NOTICE_CONTACT_OPTIONS = [
    { label: 'Property Assistant', value: 'PROPERTY_ADMIN' },
    { label: 'Property Manager', value: 'PROPERTY_MANAGER' },
    { label: 'Leasing Consultant', value: 'PROPERTY_AGENT' },
    { label: 'Regional Manager', value: 'REGIONAL_MANAGER' },
    { label: 'Designated Contact', value: 'DESIGNATED_CONTACT' },
]

export const REPORT_FREQ_VALUES = new Set(['WEEKLY', 'MONTHLY', 'NONE'])

/** Angular `company-edit.component.html` — First Reminder tooltip text. */
export const TOOLTIP_REMINDER_FIRST =
    'This notification is sent out only to Community Managers/Staff Set the day that you would like this notice to go out.'

/** Angular — Second Reminder tooltip text. */
export const TOOLTIP_REMINDER_SECOND =
    'This notification is sent out only to Community Managers/Staff chosen in the box below. It is defaulted to be sent of the 14thday of each month unless a different day is chosen in the dropdown below.'

// ── Activity tab defaults ────────────────────────────────────────────────────

export const ACTIVITY_INITIAL_PAGINATION: ActivityInitialPagination = {
    page: 1,
    perPage: 15,
    sortBy: 'desc',
    orderBy: 'log_id',
}

// ── Company info / address ───────────────────────────────────────────────────

export const MAILING_ADDRESS_FORM_FIELDS = [
    'mailing_address1',
    'mailing_address2',
    'mailing_city',
    'mailing_state',
    'mailing_zipcode',
] as const satisfies ReadonlyArray<keyof CompanyAddFormValues>

/** company-edit.component.ts geocodeAddress — first toast + map banner */
export const ADDRESS_VERIFY_HTML =
    'Warning: Unable to verify address.<br>If you know this address is accurate or is not yet in Google Maps, you may still continue and save this record'

export const ERROR_ADDRESS_VERIFY =
    'Warning: Unable to verify address. Save it if you have verified it. You may need to enter the Latitude and Longitude manually.'

export const PHONE_FORMAT_ERROR =
    'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'

/** Same message as `companyAddSchema` zipcode refine in `companyValidators.ts`. */
export const ZIPCODE_FORMAT_ERROR =
    'Enter Valid Zipcode, Should match pattern [XXXXX] or [XXXXX-XXXX].'

/** Angular `notifyError`: `error.message` includes this substring and `error.data` lists other companies. */
export const DUPLICATE_COMPANY_MESSAGE_SUBSTR = 'company with this address already'

// ── Logo upload ──────────────────────────────────────────────────────────────

export const LOGO_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png'

export const ALLOWED_LOGO_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png'])

export const MAX_LOGO_SIZE_MB = 2

export const LARGE_LOGO_CONFIRM_MB = 1

// ── Payments ─────────────────────────────────────────────────────────────────

export const PAYMENT_FILE_TYPES = { ACH: 'ACH', CANCEL_CHECK: 'CANCEL_CHECK', W9: 'W9' } as const

export const MAX_PAYMENT_FILE_SIZE_MB = 2

export const MAX_PROPERTIES_SCROLL = 400

export const ACH_PAYMENT_FORM_HELP_TEXT =
    'In order to authorize Liberty Rent to send your payment using ACH, you will need to download and complete the attached form. Once completed, upload it here along with a cancelled check for the ACH account.'

export const PAYMENT_ADDR_FIELD_ORDER = [
    'payment_address',
    'payment_city',
    'payment_state',
    'payment_zipcode',
] as const satisfies ReadonlyArray<PaymentAddrFieldKey>

export const PAY_ADDR_INVALID_RING =
    'border-red-500 ring-2 ring-red-500/25 focus:border-red-500 focus:ring-red-500/30'

export const PAYMENT_FORM_STATE_KEYS = [
    'payment_method',
    'verify',
    'payment_method_to_property',
    'routing_number',
    'account_number',
    'property_method',
    'company_status',
    'payee_name',
    'payment_address',
    'payment_shop',
    'payment_city',
    'payment_state',
    'payment_zipcode',
    'other_payee_name',
] as const

// ── Config / Yardi ───────────────────────────────────────────────────────────

export const YARDI_ROW_ID_KEY = '_yardiRowId' as const

export const LR_ROW_ID_KEY = '_lrRowId' as const

/** Angular `event_reasons`: `Validators.maxLength(240)` on the form control. */
export const EVENT_REASONS_MAX_LENGTH = 240

export const LR_EVENT_API_KEYS = ['event_type', 'lr_action', 'event_reasons'] as const

export const YARDI_EVENT_API_KEYS = [
    'event_type',
    'event_criteria',
    'lr_action',
    'event_reasons',
] as const

// ── Notes attachments ────────────────────────────────────────────────────────

export const NOTES_ATTACHMENT_FILE_TYPE = 'ADDITIONAL_ATTACHMENTS'

export const NOTES_ATTACHMENT_MODULE = 'NOTES'

// ── Email / attachments ──────────────────────────────────────────────────────

export const MAX_EMAIL_ATTACHMENT_MB = 10

// ── POST payload helpers ─────────────────────────────────────────────────────

/** Keys that must not be sent on company update (separate endpoints or read-only). */
export const COMPANY_POST_EXCLUDED_TOP_LEVEL_KEYS = new Set([
    'property_payment',
    'salesperson',
    'accountmanager',
    'company_staff',
    'staff',
    'owners',
    'favorite',
])

/**
 * Field names Angular `editCompany()` flattens with `multiSelect()` before FormData POST.
 */
export const COMPANY_POST_ANGULAR_MULTISELECT_KEYS = new Set([
    'listed_ids',
    'first_notice_contact',
    'second_notice_contact',
    'third_notice_contact',
    'notice_notice_contact',
    'active_default_first_notice_contact',
    'active_default_second_notice_contact',
    'active_default_third_notice_contact',
    'property_mgmt_system',
    'resident_screening',
])
